// src/publish/blocks.js — the neutral block model every output is written from.
//
// A backend field is read ONCE (normalize.js) into this vocabulary; from there
// on no output (plain text, Markdown, HTML, CSV, later .docx and PDF) ever sees
// HTML or Markdown again. Everything in src/publish/ speaks exactly these shapes:
//
//   Inline runs
//     { type: 'text',   text }                a literal string, no markup inside
//     { type: 'strong', runs }                bold — nested runs
//     { type: 'em',     runs }                italic — nested runs
//     { type: 'link',   href, runs }          link text + destination
//     { type: 'cite',   n }                   a "[N]" citation marker (n integer)
//     { type: 'br' }                          a line break inside the same paragraph
//
//   Blocks
//     { type: 'heading',   level: 1|2|3, runs }
//     { type: 'paragraph', runs }
//     { type: 'list',      ordered: boolean, items: runs[][] }
//     { type: 'qa',        question: runs, answer: blocks }
//     { type: 'slide',     number, slideType: 'cover'|'content'|'cta'|null,
//                          headline: runs, body: blocks, visualCue: string|null }
//
// This file holds the constructors and the few helpers every builder and
// serialiser needs (literal runs from a string, the plain text of a run list,
// shape guards). Parsing markup lives in normalize.js; writing lives in
// serialize/*. Nothing here touches i18n or the DOM.

export const RUN_TYPES = ['text', 'strong', 'em', 'link', 'cite', 'br'];
export const BLOCK_TYPES = ['heading', 'paragraph', 'list', 'qa', 'slide'];
export const SLIDE_TYPES = ['cover', 'content', 'cta'];

// ─── Inline run constructors ─────────────────────────────────────────────────

export const text = (s) => ({ type: 'text', text: String(s ?? '') });
export const strong = (runs) => ({ type: 'strong', runs: asRuns(runs) });
export const em = (runs) => ({ type: 'em', runs: asRuns(runs) });
export const link = (href, runs) => ({ type: 'link', href: String(href ?? ''), runs: asRuns(runs) });
export const cite = (n) => ({ type: 'cite', n: Number(n) });
export const br = () => ({ type: 'br' });

/**
 * Accepts a string, a single run or a run list and returns a run list. A string
 * is LITERAL (no markup parsing — use normalize.inlineRuns for that); its
 * newlines become br runs.
 */
export function asRuns(value) {
  if (value == null) return [];
  if (typeof value === 'string') return textRuns(value);
  if (Array.isArray(value)) return value.filter(isRun);
  return isRun(value) ? [value] : [];
}

/** Literal string → runs: "\n" becomes a br run, everything else is text. */
export function textRuns(s) {
  if (s == null) return [];
  const str = String(s).replace(/\r\n?/g, '\n');
  const out = [];
  const lines = str.split('\n');
  lines.forEach((line, i) => {
    if (i > 0) out.push(br());
    if (line) out.push(text(line));
  });
  return out;
}

// ─── Block constructors ──────────────────────────────────────────────────────

export const heading = (level, runs) => ({ type: 'heading', level: clampLevel(level), runs: asRuns(runs) });
export const paragraph = (runs) => ({ type: 'paragraph', runs: asRuns(runs) });
export const list = (items, ordered = false) => ({
  type: 'list',
  ordered: Boolean(ordered),
  items: (Array.isArray(items) ? items : []).map(asRuns),
});
export const qa = (question, answer) => ({ type: 'qa', question: asRuns(question), answer: asBlocks(answer) });
export const slide = ({ number, slideType = null, headline, body, visualCue = null } = {}) => ({
  type: 'slide',
  number: Number(number) || 0,
  slideType: SLIDE_TYPES.includes(slideType) ? slideType : null,
  headline: asRuns(headline),
  body: asBlocks(body),
  visualCue: visualCue == null || visualCue === '' ? null : String(visualCue),
});

/** A string becomes one paragraph (literal); blocks pass through. */
export function asBlocks(value) {
  if (value == null) return [];
  if (typeof value === 'string') return value.trim() ? [paragraph(value)] : [];
  if (Array.isArray(value)) return value.filter(isBlock);
  return isBlock(value) ? [value] : [];
}

function clampLevel(level) {
  const n = Number(level);
  if (!Number.isFinite(n)) return 2;
  return Math.min(3, Math.max(1, Math.round(n)));
}

// ─── Guards ──────────────────────────────────────────────────────────────────

export const isRun = (r) => Boolean(r) && typeof r === 'object' && RUN_TYPES.includes(r.type);
export const isBlock = (b) => Boolean(b) && typeof b === 'object' && BLOCK_TYPES.includes(b.type);

// ─── Text of a run list ──────────────────────────────────────────────────────

/**
 * The characters a reader sees in a run list: markup gone, br → "\n", a link
 * is its text only, a cite is "[N]" when `cites` is true and nothing otherwise
 * (a dropped cite takes the blank before it along: "persone [1]." → "persone.").
 * Serialisers build on this; it is the plain text WITHOUT the "testo (URL)"
 * rule, which belongs to serialize/plain.js.
 */
export function runsToText(runs, { cites = false } = {}) {
  let s = '';
  for (const r of asRuns(runs)) {
    switch (r.type) {
      case 'text': s += r.text; break;
      case 'br': s += '\n'; break;
      case 'cite': s = cites ? `${s}[${r.n}]` : dropCiteBlank(s); break;
      case 'strong':
      case 'em':
      case 'link': s += runsToText(r.runs, { cites }); break;
      default: break;
    }
  }
  return s;
}

/** The text so far without the blank that led to a dropped "[N]" (never a line break). */
export function dropCiteBlank(s) {
  return s.replace(/[ \t ]+$/, '');
}

/** True when the run list carries no visible character. */
export function runsAreEmpty(runs) {
  return runsToText(runs, { cites: true }).trim() === '';
}

/** Drops blocks that carry no text at all (an empty paragraph, an empty list). */
export function compactBlocks(blocks) {
  return asBlocks(blocks).filter((b) => {
    switch (b.type) {
      case 'heading':
      case 'paragraph': return !runsAreEmpty(b.runs);
      case 'list': return b.items.some((it) => !runsAreEmpty(it));
      case 'qa': return !runsAreEmpty(b.question) || b.answer.length > 0;
      case 'slide': return true;
      default: return false;
    }
  });
}
